import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Edit, Trash2, Power, Search, X } from 'lucide-react';
import CyberneticLoader from '@/components/CyberneticLoader';
import ExpressBadge from '@/components/ExpressBadge';

const AdminPanel = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dispensaries');
  const [dispensaries, setDispensaries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit modal state
  const [editModal, setEditModal] = useState(false);
  const [editingDispensary, setEditingDispensary] = useState(null);
  
  // Filters for orders
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    status: '',
    dispensary_id: '',
    rider_id: '',
    sort_by_amount: ''
  });

  useEffect(() => {
    if (activeTab === 'dispensaries') {
      fetchDispensaries();
    } else {
      fetchOrders();
    }
  }, [activeTab]);

  const fetchDispensaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/dispensaries');
      setDispensaries(response.data);
    } catch (error) {
      console.error('Error fetching dispensaries:', error);
      toast.error('Error al cargar dispensarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status) params.status = filters.status;
      if (filters.dispensary_id) params.dispensary_id = filters.dispensary_id;
      if (filters.rider_id) params.rider_id = filters.rider_id;
      if (filters.sort_by_amount) params.sort_by_amount = filters.sort_by_amount;

      const response = await axios.get('/admin/orders', { params });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await axios.patch(`/admin/dispensaries/${id}/status`);
      toast.success(`Dispensario ${!currentStatus ? 'activado' : 'desactivado'}`);
      fetchDispensaries();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleDeleteDispensary = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este dispensario permanentemente?')) {
      return;
    }

    try {
      await axios.delete(`/admin/dispensaries/${id}`);
      toast.success('Dispensario eliminado');
      fetchDispensaries();
    } catch (error) {
      console.error('Error deleting dispensary:', error);
      toast.error('Error al eliminar dispensario');
    }
  };

  const handleEditDispensary = (dispensary) => {
    setEditingDispensary({ ...dispensary });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(
        `/admin/dispensaries/${editingDispensary.id}`,
        {
          name: editingDispensary.name,
          phone: editingDispensary.phone,
          email: editingDispensary.email,
          address: editingDispensary.address
        }
      );
      toast.success('Dispensario actualizado');
      setEditModal(false);
      fetchDispensaries();
    } catch (error) {
      console.error('Error updating dispensary:', error);
      toast.error('Error al actualizar dispensario');
    }
  };

  const handleExportOrders = async () => {
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status) params.status = filters.status;
      if (filters.dispensary_id) params.dispensary_id = filters.dispensary_id;
      if (filters.rider_id) params.rider_id = filters.rider_id;

      const response = await axios.get('/admin/orders/export', {
        params,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pedidos_uid_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Archivo Excel descargado');
    } catch (error) {
      console.error('Error exporting orders:', error);
      toast.error('Error al exportar pedidos');
    }
  };

  const filteredDispensaries = dispensaries.filter(disp =>
    disp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    disp.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOrders = orders.filter(order =>
    order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.dispensary_name && order.dispensary_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2A24' }}>
            Panel de Administración
          </h1>
          <p className="text-gray-600 mt-2">
            Control total de dispensarios y pedidos
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b-2" style={{ borderColor: '#D4AF37' }}>
          <button
            onClick={() => setActiveTab('dispensaries')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'dispensaries'
                ? 'border-b-4 text-white'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            style={{
              borderColor: activeTab === 'dispensaries' ? '#D4AF37' : 'transparent',
              backgroundColor: activeTab === 'dispensaries' ? '#224049' : 'transparent',
              borderRadius: '8px 8px 0 0'
            }}
          >
            Dispensarios
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'orders'
                ? 'border-b-4 text-white'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            style={{
              borderColor: activeTab === 'orders' ? '#D4AF37' : 'transparent',
              backgroundColor: activeTab === 'orders' ? '#224049' : 'transparent',
              borderRadius: '8px 8px 0 0'
            }}
          >
            Pedidos
          </button>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-lg p-6" style={{ border: '2px solid #D4AF37', overflowX: 'auto' }}>
          {/* Search and Actions Bar */}
          <div className="flex justify-between items-center mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-2 rounded-lg focus:outline-none"
                style={{ borderColor: '#E8E8E8', focusBorderColor: '#D4AF37' }}
              />
            </div>
            
            {activeTab === 'orders' && (
              <button
                onClick={handleExportOrders}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white transition-all hover:shadow-lg"
                style={{ backgroundColor: '#D4AF37', color: '#224049' }}
              >
                <Download size={20} />
                Exportar a Excel
              </button>
            )}
          </div>

          {/* Filters for Orders */}
          {activeTab === 'orders' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#224049' }}>
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                  className="w-full px-3 py-2 border-2 rounded-lg"
                  style={{ borderColor: '#E8E8E8' }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#224049' }}>
                  Fecha Fin
                </label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                  className="w-full px-3 py-2 border-2 rounded-lg"
                  style={{ borderColor: '#E8E8E8' }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#224049' }}>
                  Estado
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full px-3 py-2 border-2 rounded-lg"
                  style={{ borderColor: '#E8E8E8' }}
                >
                  <option value="">Todos</option>
                  <option value="CREATED">Creado</option>
                  <option value="READY_FOR_PICKUP">Listo para recoger</option>
                  <option value="IN_CUSTODY">En custodia</option>
                  <option value="EN_ROUTE">En ruta</option>
                  <option value="ARRIVED">Arribado</option>
                  <option value="DELIVERED">Entregado</option>
                  <option value="FAILED_DELIVERY">Fallo en entrega</option>
                  <option value="CANCELLED">Cancelado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#224049' }}>
                  Ordenar por Monto
                </label>
                <select
                  value={filters.sort_by_amount}
                  onChange={(e) => setFilters({...filters, sort_by_amount: e.target.value})}
                  className="w-full px-3 py-2 border-2 rounded-lg"
                  style={{ borderColor: '#E8E8E8' }}
                >
                  <option value="">Sin ordenar</option>
                  <option value="asc">Menor a Mayor</option>
                  <option value="desc">Mayor a Menor</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={fetchOrders}
                  className="w-full px-4 py-2 rounded-lg font-semibold text-white transition-all"
                  style={{ backgroundColor: '#224049' }}
                >
                  Aplicar Filtros
                </button>
              </div>
            </div>
          )}

          {/* Dispensaries Table */}
          {activeTab === 'dispensaries' && (
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-12">
                  <CyberneticLoader size={80} message="Cargando dispensarios..." />
                </div>
              ) : (
                <table className="w-full" style={{ minWidth: '800px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#224049', color: '#D4AF37' }}>
                      <th className="px-4 py-3 text-left">Nombre</th>
                      <th className="px-4 py-3 text-left">Email</th>
                      <th className="px-4 py-3 text-left">Teléfono</th>
                      <th className="px-4 py-3 text-center">Total Pedidos</th>
                      <th className="px-4 py-3 text-center">Estado</th>
                      <th className="px-4 py-3 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDispensaries.map((disp, index) => (
                      <tr
                        key={disp.id}
                        className="border-b hover:bg-gray-50"
                        style={{ borderColor: '#E8E8E8' }}
                      >
                        <td className="px-4 py-3 font-semibold">{disp.name}</td>
                        <td className="px-4 py-3">{disp.email}</td>
                        <td className="px-4 py-3">{disp.phone}</td>
                        <td className="px-4 py-3 text-center">{disp.total_orders || 0}</td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className="px-3 py-1 rounded-full text-sm font-semibold"
                            style={{
                              backgroundColor: disp.is_active ? '#D4AF37' : '#CCCCCC',
                              color: disp.is_active ? '#224049' : '#666666'
                            }}
                          >
                            {disp.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEditDispensary(disp)}
                              className="p-2 rounded-lg hover:bg-blue-100 transition"
                              title="Editar"
                            >
                              <Edit size={18} style={{ color: '#224049' }} />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(disp.id, disp.is_active)}
                              className="p-2 rounded-lg hover:bg-yellow-100 transition"
                              title={disp.is_active ? 'Desactivar' : 'Activar'}
                            >
                              <Power size={18} style={{ color: '#D4AF37' }} />
                            </button>
                            <button
                              onClick={() => handleDeleteDispensary(disp.id)}
                              className="p-2 rounded-lg hover:bg-red-100 transition"
                              title="Eliminar"
                            >
                              <Trash2 size={18} style={{ color: '#DC2626' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Orders Table */}
          {activeTab === 'orders' && (
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-12">
                  <CyberneticLoader size={80} message="Cargando pedidos..." />
                </div>
              ) : (
                <table className="w-full text-sm" style={{ minWidth: '900px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#224049', color: '#D4AF37' }}>
                      <th className="px-3 py-2 text-left">ID</th>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Dispensario</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-left">Rider</th>
                      <th className="px-3 py-2 text-right">Monto</th>
                      <th className="px-3 py-2 text-left">Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr
                        key={order.id}
                        className="border-b hover:bg-gray-50"
                        style={{ borderColor: '#E8E8E8' }}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{order.id.substring(0, 8)}</td>
                        <td className="px-3 py-2">
                          {new Date(order.created_at).toLocaleDateString('es-MX')}
                        </td>
                        <td className="px-3 py-2">{order.dispensary_name || 'N/A'}</td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: '#E8E8E8' }}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">{order.rider_name || 'Sin asignar'}</td>
                        <td className="px-3 py-2 text-right font-semibold">
                          ${order.total_fee?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-3 py-2">
                          {order.customer?.name || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && editingDispensary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" style={{ border: '2px solid #D4AF37' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold" style={{ color: '#224049' }}>
                Editar Dispensario
              </h3>
              <button onClick={() => setEditModal(false)}>
                <X size={24} style={{ color: '#666' }} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#224049' }}>
                  Nombre
                </label>
                <input
                  type="text"
                  value={editingDispensary.name}
                  onChange={(e) => setEditingDispensary({...editingDispensary, name: e.target.value})}
                  className="w-full px-4 py-2 border-2 rounded-lg"
                  style={{ borderColor: '#E8E8E8' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#224049' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={editingDispensary.email}
                  onChange={(e) => setEditingDispensary({...editingDispensary, email: e.target.value})}
                  className="w-full px-4 py-2 border-2 rounded-lg"
                  style={{ borderColor: '#E8E8E8' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1" style={{ color: '#224049' }}>
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={editingDispensary.phone}
                  onChange={(e) => setEditingDispensary({...editingDispensary, phone: e.target.value})}
                  className="w-full px-4 py-2 border-2 rounded-lg"
                  style={{ borderColor: '#E8E8E8' }}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setEditModal(false)}
                  className="flex-1 px-4 py-2 border-2 rounded-lg font-semibold"
                  style={{ borderColor: '#224049', color: '#224049' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 px-4 py-2 rounded-lg font-semibold text-white"
                  style={{ backgroundColor: '#D4AF37', color: '#224049' }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminPanel;
